import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edge, Node } from "reactflow";
import { RootSchema } from "../redux/reducers";
import { SMTP_PROVIDERS } from "../config/defaults";
import { LanguageSchema } from "../i18n";
import { v4 as uuidv4 } from "uuid";
import { downloadFile, isFeatureEnabled } from "../utils/Workflow";
import { SUCCESS_HANDLING_PROPERTY_ID, GetSuccessDefaultsForNode } from "../utils/Workflow/successMap";
import { getSecondFactorAuth } from "../redux/features/security/multiFactorAuthentication/secondFactorAuthentication";
import { SUPPORTED_PROVIDERS, WorkflowNodeConfig } from "../utils/constants";
import { getAppFeatureAction } from "../redux/actions/authActions";
import { getCaptchaSettingsAction } from "../redux/actions/securityConfigurationActions";

interface ContextProps {
  selectedNode?: any;
  setSelectedNode?: any;
  tree?: any;
  setTree?: any;
  nodes?: any;
  setNodes?: any;
  edges?: any;
  setEdges?: any;
  dragOverParentNode?: any;
  setDragOverParentNode?: any;
  setDraggedNode?: any;
  draggedNode?: any;
  policies?: any;
  setPolicies?: any;
  validateNodeProperties?: any;
  validateNodePropertyValues?: any;
  isPropertyConfigError?: any;
  setIsPropertyConfigError?: any;
  updateStateOnNodesDelete?: any;
  updateDynamicProperties?: any;
  checkParentNodeDrop?: any;
  nodeValidator?: any;
  updatedTree?: any;
  setUpdatedTree?: any;
  isValidationError?: any;
  setIsValidationError?: any;
  isPolicyValidationError?: any;
  setIsPolicyValidationError?: any;
  checkValidationError?: any;
  checkPolicyValidationError?: any;
  isOpenWindow?: any;
  setIsOpenwindow?: any;
  CustomNodeToTreeNode?: any;
  duplicateNode?: any;
  getNodeById?: any;
  getNodeLabelById?: any;
  disallowedConnections?: any;
  isWorkflowChanged?: any;
  getJSON?: any;
  readOnly?: boolean;
  highlightedNodeId?: string | null;
  setHighlightedNodeId?: any;
  secondHighlightedNodeId?: string | null;
  setSecondHighlightedNodeId?: any;
  /**
   * When true, clicking a node's header label highlights its incoming/outgoing
   * edges (and shows the shortest path between two clicked nodes).
   * When false, clicking a node header has no visual effect on edges.
   * Persisted in localStorage so the preference survives page refresh.
   */
  isHighlightModeEnabled?: boolean;
  setIsHighlightModeEnabled?: (enabled: boolean) => void;
}

const getJSON = (e: any, shouldDownload = true, nodes, tree, edges, policies, reactFlowInstanceState, workflowName, flowkey) => {
  // e && e.preventDefault();
  const mainNodes = nodes.filter((node: any) => {
    return node.parentNode === undefined;
  });
  const innerNodes = nodes.filter((node: any) => {
    return node.parentNode !== undefined;
  });
  const requiredMainNodes = mainNodes.reduce((accumulator: any, value: any) => {
    return { ...accumulator, [value.id]: value };
  }, {});
  const requiredInnerNodes = innerNodes.reduce((accumulator: any, value: any) => {
    return { ...accumulator, [value.id]: value };
  }, {});

  const data = {
    tree: tree,
    nodes: requiredMainNodes,
    innerNodes: requiredInnerNodes,
    edges: edges,
    policies: policies,
    viewport: reactFlowInstanceState && typeof reactFlowInstanceState.getViewport === "function" ? reactFlowInstanceState.getViewport() : undefined
  };
  if (shouldDownload) {
    downloadFile({
      data: JSON.stringify(data),
      fileName: `${workflowName || flowkey}.json`,
      fileType: "text/json"
    });
  } else {
    return data;
  }
};

// Utility to pick only allowed fields from an object
function pick(obj: any, keys: string[]) {
  const result: any = {};
  for (const key of keys) {
    if (obj.hasOwnProperty(key)) {
      result[key] = obj[key];
    }
  }
  return result;
}

function cleanEdges(edges: any[]): any[] {
  if (!Array.isArray(edges)) return [];
  return edges.map((edge) => pick(edge, ["id", "source", "target"]));
}

const SUCCESS_HANDLING_ELEMENT_TYPE = "successarray";

// Returns only the successarray entries whose text differs from the node's default.
// This prevents "Save to State" (which writes defaults) from creating false positives
// in isWorkflowChanged, while still detecting intentional user edits.
function normalizeSuccessArray(items: any[], nodeType: string): any[] {
  if (!Array.isArray(items)) return [];
  const defaults = GetSuccessDefaultsForNode(nodeType);
  // Node type not in successMessageMap — no defaults to compare against,
  // so fall back to stripping entirely (original behaviour, no false positives).
  if (Object.keys(defaults).length === 0) return [];
  return items.filter((item) => item && item.key && item.text !== defaults[item.key]);
}

function sanitizeWorkflowProperties(properties: any[], nodeType?: string): any[] {
  if (!Array.isArray(properties)) return [];
  return properties
    .filter((property) => {
      if (!property || typeof property !== "object") return true;
      // successarray is normalized below — let it through the filter for now
      if (property.elementType === SUCCESS_HANDLING_ELEMENT_TYPE) return true;
      return property.id !== SUCCESS_HANDLING_PROPERTY_ID;
    })
    .map((property) => {
      if (property.elementType === SUCCESS_HANDLING_ELEMENT_TYPE && nodeType) {
        return { ...property, value: normalizeSuccessArray(property.value, nodeType) };
      }
      return property;
    })
    .filter((property) => {
      // Strip successarray from comparison when no non-default entries remain.
      // This prevents false positives when "Save to State" writes defaults onto a
      // node that had no successarray saved yet (e.g. older workflows).
      if (property.elementType === SUCCESS_HANDLING_ELEMENT_TYPE) {
        return Array.isArray(property.value) && property.value.length > 0;
      }
      return true;
    });
}

function cleanNode(node: any, includeProperties: boolean = true, otherNode?: any): any {
  // For main nodes: pick data, id, nodes, output, position, positionAbsolute, type
  const cleaned = pick(node, ["data", "id", "nodes", "output", "position", "positionAbsolute", "type"]);
  // Always ensure data is an object
  if (!cleaned.data) cleaned.data = {};
  // Normalize nodes: remove if null or undefined
  if (cleaned.hasOwnProperty("nodes") && (cleaned.nodes === null || typeof cleaned.nodes === "undefined")) {
    delete cleaned.nodes;
  }
  const thisProps = cleaned.data.properties;
  const otherProps = otherNode && otherNode.data ? otherNode.data.properties : undefined;
  if (!includeProperties || !Array.isArray(thisProps) || !Array.isArray(otherProps)) {
    // Remove properties if not included or not an array in both
    const { properties, ...rest } = cleaned.data;
    cleaned.data = rest;
  } else if (includeProperties && typeof thisProps === "undefined") {
    // If properties is expected but missing, default to []
    cleaned.data.properties = [];
  } else if (includeProperties && Array.isArray(cleaned.data.properties)) {
    cleaned.data = {
      ...cleaned.data,
      properties: sanitizeWorkflowProperties(cleaned.data.properties, cleaned.type)
    };
  }
  return cleaned;
}

function cleanNodesObj(nodesObj: any, isInner: boolean = false, otherNodesObj?: any): any {
  if (!nodesObj || typeof nodesObj !== "object") return nodesObj;
  const cleaned: any = {};
  // Collect all keys from both node sets, sort for stable order
  const allKeys = Array.from(new Set([...Object.keys(nodesObj || {}), ...(otherNodesObj ? Object.keys(otherNodesObj) : [])])).sort();
  for (const key of allKeys) {
    const node = nodesObj[key];
    const otherNode = otherNodesObj ? otherNodesObj[key] : undefined;
    // Only add key if node exists on at least one side
    if (!node && !otherNode) {
      continue;
    }
    // If node exists only on one side, clean as-is (with properties)
    if (!node || !otherNode) {
      cleaned[key] = isInner ? cleanInnerNode(node || otherNode) : cleanNode(node || otherNode, true);
      continue;
    }
    // If node exists on both sides, apply includeProperties logic
    let includeProperties = true;
    const thisHasProps = node && node.data && Array.isArray(node.data.properties);
    const otherHasProps = otherNode && otherNode.data && Array.isArray(otherNode.data.properties);
    if (!(thisHasProps && otherHasProps)) {
      includeProperties = false;
    }
    cleaned[key] = isInner ? cleanInnerNode(node) : cleanNode(node, includeProperties, otherNode);
  }
  return cleaned;
}

function cleanInnerNode(node: any): any {
  // For inner nodes: pick id, isChildNode, parentNode, position, positionAbsolute, type, data
  const picked = pick(node, ["id", "isChildNode", "parentNode", "position", "positionAbsolute", "type", "data"]);
  if (picked.data && Array.isArray(picked.data.properties)) {
    picked.data = {
      ...picked.data,
      properties: sanitizeWorkflowProperties(picked.data.properties, picked.type)
    };
  }
  // Sort keys for stable order
  return Object.keys(picked)
    .sort()
    .reduce((acc, key) => {
      acc[key] = picked[key];
      return acc;
    }, {} as any);
}

function isWorkflowChanged(data: any, currentWorkflow: any): boolean {
  if (!data || !currentWorkflow) return false;
  try {
    const currentData = {
      tree: currentWorkflow.tree,
      nodes: cleanNodesObj(currentWorkflow.nodes, false, data.nodes),
      innerNodes: cleanNodesObj(currentWorkflow.innerNodes, true, data.innerNodes),
      edges: cleanEdges(currentWorkflow.edges),
      policies: currentWorkflow.policies
    };
    const jsonData = {
      tree: data.tree,
      nodes: cleanNodesObj(data.nodes, false, currentWorkflow.nodes),
      innerNodes: cleanNodesObj(data.innerNodes, true, currentWorkflow.innerNodes),
      edges: cleanEdges(data.edges),
      policies: data.policies
    };
    let realChange = false;
    const findDiff = (a: any, b: any) => {
      if (realChange) return;
      if (a === b) return;
      if (typeof a !== typeof b) {
        realChange = true;
        return;
      }
      if (a === null || b === null) {
        if (a !== b) {
          realChange = true;
        }
        return;
      }
      if (typeof a !== "object") {
        realChange = true;
        return;
      }
      const aKeys = Object.keys(a || {});
      const bKeys = Object.keys(b || {});
      for (const key of new Set([...aKeys, ...bKeys])) {
        const hasA = Object.prototype.hasOwnProperty.call(a, key);
        const hasB = Object.prototype.hasOwnProperty.call(b, key);
        if (!hasA || !hasB) {
          realChange = true;
          if (realChange) return;
          continue;
        }
        findDiff(a[key], b[key]);
        if (realChange) return;
      }
    };
    findDiff(currentData, jsonData);
    return realChange;
  } catch (e) {
    return false;
  }
}
const StateContext = createContext<ContextProps>({});

interface Tree {
  entryNodeId: string;
  nodes: any;
  staticNodes: {
    successFinalNode: { position: { x: number; y: number } };
    failureFinalNode: { position: { x: number; y: number } };
    initialNode: { position: { x: number; y: number } };
  };
}
const initialTree: Tree = {
  entryNodeId: "",
  nodes: {},
  staticNodes: {
    successFinalNode: { position: { x: 100, y: 300 } },
    failureFinalNode: { position: { x: 400, y: 300 } },
    initialNode: { position: { x: 250, y: -300 } }
  }
};
interface ContextProviderProps {
  children?: ReactNode;
}

type CustomNode = Node & {
  output?: any;
  nodes?: any;
};

export const createInitialNodes = (i18n: LanguageSchema): CustomNode[] => [
  {
    id: "initialNode",
    type: "initial",
    data: { label: "initial node" },
    position: { x: -400, y: -50 }
  },
  {
    id: "SuccessFinalNode",
    type: "finalPositive",
    data: {
      label: "Success",
      description: "Denotes that the workflow ended in success.",
      properties: [
        {
          id: "1",
          type: "text",
          value: "",
          label: "Redirect URL",
          isURL: true,
          elementType: "redirecturl",
          tooltipMessage: i18n.WORKFLOW_REDIRECT_URL_TEXT
        },
        {
          id: "2",
          type: "text",
          value: "",
          label: "Success Message",
          elementType: "finalmessage",
          tooltipMessage: i18n.WORKFLOW_SUCCESS_FINAL_MESSAGE
        },
        {
          id: "3",
          type: "text",
          value: "",
          label: "Home Page Button Text",
          elementType: "homepagebuttontext",
          tooltipMessage: i18n.WORKFLOW_HOME_PAGE_BUTTON_TEXT
        }
      ]
    },
    position: { x: 300, y: -150 }
  },
  {
    id: "FailureFinalNode",
    type: "finalNegative",
    data: {
      label: "Failure",
      description: "Denotes that the workflow ended in failure.",
      properties: [
        {
          id: "1",
          type: "text",
          value: "",
          label: "Redirect URL",
          isURL: true,
          elementType: "redirecturl",
          tooltipMessage: i18n.WORKFLOW_REDIRECT_URL_TEXT
        },
        {
          id: "2",
          type: "text",
          value: "",
          label: "Failure Message",
          elementType: "finalmessage",
          tooltipMessage: i18n.WORKFLOW_FAILURE_FINAL_MESSAGE
        },
        {
          id: "3",
          type: "text",
          value: "",
          label: "Home Page Button Text",
          elementType: "homepagebuttontext",
          tooltipMessage: i18n.WORKFLOW_HOME_PAGE_BUTTON_TEXT
        }
      ]
    },
    position: { x: 300, y: 150 }
  }
];

export const initialPolicies = {
  email: [
    {
      id: "1",
      type: "checkbox",
      value: false,
      label: "Should Restrict",
      elementType: "shouldrestrict"
    },
    {
      id: "2",
      type: "multivalue",
      value: "",
      label: "BlackList Email/Domain",
      elementType: "blacklistemaildomain"
    },
    {
      id: "3",
      type: "multivalue",
      value: "",
      label: "WhiteList Email/Domain",
      elementType: "whitelistemaildomain"
    }
  ],

  password: [
    {
      id: "4",
      type: "text",
      value: "min_length[6]|max_length[32]|required",
      label: "Password Validation",
      elementType: "rules",
      tooltipMessage: "Validate password property must be enabled"
    },
    {
      id: "5",
      type: "selectinput",
      inputType: "number",
      options: [
        { text: "Days", value: "Days" },
        { text: "Month", value: "Month" },
        { text: "Year", value: "Year" }
      ],
      value: "",
      label: "Password Expiration",
      elementType: "passwordexpiration",
      min: 1,
      max: 365
    },
    {
      id: "6",
      type: "text",
      value: "",
      label: "Password History",

      elementType: "passwordhistory"
    },
    {
      id: "7",
      type: "checkbox",
      value: false,
      label: "Common Password Protection",

      elementType: "confirmpasswordprotection"
    },
    {
      id: "8",
      type: "checkbox",
      value: false,
      label: "Dictionary Password Prevention",

      elementType: "dictionarypasswordprevention"
    },
    {
      id: "9",
      type: "checkbox",
      value: false,
      label: "Profile Field Password Prevention",

      elementType: "profilefieldpasswordprevention"
    }
  ],

  username: [
    {
      id: "10",
      type: "checkbox",
      value: false,
      label: "Is Primary",
      elementType: "isprimary"
    },
    {
      id: "11",
      type: "checkbox",
      value: false,
      label: "Duplicate Email",
      primaryParentId: "10",
      parentPropertyId: "10",
      parentPropertyValue: true,
      elementType: "duplicateemail"
    },
    {
      id: "12",
      type: "checkbox",
      value: false,
      label: "Case sensitive Username",

      elementType: "casesensitiveusername"
    }
  ],
  pininput: [
    {
      id: "13",
      type: "text",
      value: "",
      label: "Pin Validation",
      elementType: "rules"
    }
  ],
  phone: [
    {
      id: "14",
      type: "checkbox",
      value: false,
      label: "Is Primary",
      elementType: "isprimary"
    },

    {
      id: "15",
      type: "checkbox",
      value: false,
      label: "Country Code",
      elementType: "countrycode"
    },

    {
      id: "16",
      type: "select",
      value: "",
      primaryParentId: "15",
      parentPropertyId: "15",
      parentPropertyValue: true,
      isSearchEnabled: true,
      options: "optionsofsetcountrycode",
      label: "Set Default Country Code",
      elementType: "setdefaultcountrycode"
    },
    {
      id: "17",
      type: "multiselect",
      value: [],
      primaryParentId: "15",
      parentPropertyId: "15",
      parentPropertyValue: true,
      isSearchEnabled: true,
      options: "optionsofallowcountrycode",
      label: "Allow particular country code",
      elementType: "allowparticularcountrycode"
    }
  ]
};

export const ContextProvider = ({ children }: ContextProviderProps) => {
  const i18n = useSelector((state: RootSchema) => state.appState.i18n.languages);
  const [selectedNode, setSelectedNode] = useState(null);
  const [tree, setTree] = useState<any>(initialTree);
  const [nodes, setNodes] = useState<CustomNode[]>(createInitialNodes(i18n));
  const [edges, setEdges] = useState<Edge[]>([]);
  const [dragOverParentNode, setDragOverParentNode] = useState("");
  const [draggedNode, setDraggedNode] = useState<any>(null);
  const [policies, setPolicies] = useState<any>(initialPolicies);
  const [isPropertyConfigError, setIsPropertyConfigError] = useState(new Map());
  const [updatedTree, setUpdatedTree] = useState<any>({});
  const [isValidationError, setIsValidationError] = useState({});
  const [isPolicyValidationError, setIsPolicyValidationError] = useState({});
  const [isOpenWindow, setIsOpenwindow] = useState(false);
  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [secondHighlightedNodeId, setSecondHighlightedNodeId] = useState<string | null>(null);

  // ── Edge-highlight mode ────────────────────────────────────────────────────
  // Initialise from localStorage so the toggle persists across page refreshes.
  // Default is OFF so the editor behaves normally until the user enables it.
  const [isHighlightModeEnabled, setIsHighlightModeEnabledState] = useState<boolean>(() => localStorage.getItem("workflow_highlight_mode") === "true");

  /**
   * Toggling highlight mode also clears any active highlights so the canvas
   * never stays in a partially-highlighted state when the feature is turned off.
   */
  const setIsHighlightModeEnabled = (enabled: boolean) => {
    localStorage.setItem("workflow_highlight_mode", String(enabled));
    setIsHighlightModeEnabledState(enabled);
    if (!enabled) {
      // Clear any active selection when the feature is disabled
      setHighlightedNodeId(null);
      setSecondHighlightedNodeId(null);
    }
  };
  const allSmsProviders = useSelector((state: RootSchema) => state.platformConfiguration.smsProviderConfig);
  const allCustomSmsProviders = useSelector((state: RootSchema) => state.platformConfiguration.customSmsProviderConfig);
  const allEmailProviders = useSelector((state: RootSchema) => state.deployment.smtpSetting);
  const smsTemplates = useSelector((state: RootSchema) => state.platformConfiguration.smsTemplates);
  const allEmailTemplates = useSelector((state: RootSchema) => state.deployment.allEmailTemplates);
  const allFeatures = useSelector((state: RootSchema) => state.auth.appFeatures);
  const mfafeatures = useSelector((state: RootSchema) => state.securityConfiguration.secondFactorAuthentication);
  const captchaSetting = useSelector((state: RootSchema) => state.securityConfiguration.getCaptchaSettingsData);
  const dispatch = useDispatch();
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
  const { isTrialFlow } = useSelector((state: RootSchema) => state.appState.appUI);
  // useEffect(() => {
  //   console.log("nodes", nodes);
  // }, [nodes]);

  // useEffect(() => {
  //   console.log("tree", tree);
  // }, [tree]);

  // useEffect(() => {
  //   console.log("edges", edges);
  // }, [edges]);

  // useEffect(() => {
  //   console.log("selectedNode", selectedNode);
  // }, [selectedNode]);

  // useEffect(() => {
  //   console.log("dragOverParentNode", dragOverParentNode);
  // }, [dragOverParentNode]);

  useEffect(() => {
    if (isTrialFlow || !isLoggedIn) {
      return;
    }
    if (!allFeatures.isSuccess) {
      dispatch(getAppFeatureAction());
    }
    if (!mfafeatures.isSuccess) {
      dispatch(getSecondFactorAuth());
    }
    if (!captchaSetting.isSuccess) {
      dispatch(getCaptchaSettingsAction());
    }
  }, [allFeatures.isSuccess, mfafeatures.isSuccess, captchaSetting.isSuccess, dispatch, isTrialFlow, isLoggedIn]);
  const CustomNodeToTreeNode = (node: CustomNode) => {
    return {
      id: node.id,
      connections: node.output.map((output: any) => {
        return { [output.id]: "" };
      }),
      type: node.type
    };
  };

  const addNode = (node: CustomNode) => {
    setNodes((nds: any[]) => nds.concat(node));
  };

  const getNodeById = (id: string) => {
    return nodes.find((node: any) => node.id === id);
  };

  const checkParentNodeDrop = (newNode: any) => {
    const isDropOnParentNode = nodes.find((node: any) => {
      if (
        node.type === "webpage" &&
        newNode.isChildNode &&
        newNode.position.x >= node.position.x &&
        newNode.position.x <= node.position.x + node.width &&
        newNode.position.y >= node.position.y &&
        newNode.position.y <= node.position.y + node.height
      ) {
        // newNode.position.x = newNode.position.x - node.position.x;
        // newNode.position.y = newNode.position.y - node.position.y;
        newNode.position.x = 10;

        newNode.position.y = node.nodes ? node.nodes.length * 42 + 41 : 41;
        return node;
      }
    });

    let updatedNewNode = newNode;

    if (isDropOnParentNode) {
      const { ...childNode } = newNode;

      setDragOverParentNode("");
      setNodes((nds: any) =>
        nds.map((node: any) => {
          if (node.id === isDropOnParentNode.id) {
            const existingNodes = Array.isArray(node.nodes) ? node.nodes : [];
            const hasChild = existingNodes.some((innerNode: any) => innerNode.id === childNode.id);
            const nextNodes = hasChild
              ? existingNodes
              : [
                  ...existingNodes,
                  (({ id, type, childType, isChildNode, data }) => ({
                    id,
                    type,
                    childType,
                    isChildNode,
                    data: { label: data.label }
                  }))(childNode)
                ];

            const shouldAddOutput = childNode.type === "button" || childNode.childType === "social" || childNode.childType === "passkey";
            const existingOutput = Array.isArray(node.output) ? node.output : [];
            const hasOutput = existingOutput.some((output: any) => output.id === childNode.id);
            const nextOutput =
              shouldAddOutput && !hasOutput
                ? [
                    ...existingOutput,
                    {
                      id: childNode.id,
                      displayName: childNode.data.label
                    }
                  ]
                : existingOutput;

            return {
              ...node,
              nodes: nextNodes,
              output: nextOutput
            };
          }

          if (node.id === childNode.id) {
            return {
              ...node,
              parentNode: isDropOnParentNode.id,
              extent: "parent",
              hidden: true,
              selected: false
            };
          }

          return node;
        })
      );
      nodeValidator(newNode.id, newNode.data.properties);
      updatedNewNode = {
        ...newNode,
        parentNode: isDropOnParentNode.id,
        extent: "parent",
        hidden: true,
        selected: false
      };

      if (newNode.type === "button" || newNode.childType === "social" || newNode.childType === "passkey") {
        setTree((tr: any) => {
          if (!tr.nodes[isDropOnParentNode.id]) return tr;
          return {
            ...tr,
            nodes: {
              ...tr.nodes,
              [isDropOnParentNode.id]: {
                ...tr.nodes[isDropOnParentNode.id],
                connections: [...tr.nodes[isDropOnParentNode.id].connections, { [childNode.id]: "" }]
              }
            }
          };
        });
      }

      setEdges((eds: any) =>
        eds.map((edge: any) => {
          return edge;
        })
      );
    }

    return updatedNewNode;
  };
  const validateNodeProperties = (properties) => {
    return properties?.filter((property: any) => {
      if (
        property.required &&
        (Array.isArray(property.value) ? property.value.length === 0 : !property.value) &&
        (property.primaryParentId ? properties.find((prop: any) => prop.id === property.primaryParentId)?.value === true : false)
      ) {
        return null;
      }
      if (
        property.required &&
        (Array.isArray(property.value) ? property.value.length === 0 : !property.value) &&
        (property.parentPropertyId
          ? Array.isArray(properties.find((prop: any) => prop.id === property.parentPropertyId)?.value)
            ? Array.isArray(property.parentPropertyValue)
              ? property.parentPropertyValue.some((value) => properties.find((prop: any) => prop.id === property.parentPropertyId)?.value.includes(value))
              : properties.find((prop: any) => prop.id === property.parentPropertyId)?.value.includes(property.parentPropertyValue)
            : Array.isArray(property.parentPropertyValue)
              ? property.parentPropertyValue.some((value) => properties.find((prop: any) => prop.id === property.parentPropertyId)?.value === value)
              : properties.find((prop: any) => prop.id === property.parentPropertyId)?.value === property.parentPropertyValue
          : true)
      ) {
        return property;
      }
    }).length;
  };

  const duplicateNode = (id) => {
    const node = getNodeById(id);
    const position = {
      x: node.position.x + 100,
      y: node.position.y + 100
    };
    const childIds = node.nodes && node.nodes.map((node: any) => node.id);
    const myMap = new Map();
    const newNode = {
      ...node,
      position,
      id: uuidv4(),
      selected: false,
      dragging: false,
      nodes:
        node.nodes &&
        node.nodes.map((node: any) => {
          const id = uuidv4();
          myMap.set(node.id, id);
          return {
            ...node,
            id: id
          };
        })
    };

    addNode(newNode);

    childIds &&
      childIds.length &&
      childIds.forEach((nodeId: any) => {
        const childNode = getNodeById(nodeId);
        addNode({
          ...childNode,
          id: myMap.get(childNode.id),
          hidden: true,
          selected: false,
          dragging: false,
          parentNode: newNode.id,
          extent: "parent"
        });
      });

    setTree((tr: any) => ({
      ...tr,
      nodes: {
        ...tr.nodes,
        [newNode.id]: CustomNodeToTreeNode(newNode)
      }
    }));
  };
  const validateProviderSyncNodeProperties = (properties?: any[]) => {
    const safeProperties = Array.isArray(properties) ? properties : [];

    // 1️⃣ If provider data is not ready, do NOT fail validation
    if (!allSmsProviders && !allCustomSmsProviders && !allEmailProviders) {
      return true;
    }

    // 2️⃣ Build SMS provider options
    const allSmsProvidersOptions =
      allSmsProviders?.data?.TwilioAccountSid != null
        ? [
            {
              text: allSmsProviders.data.ProviderName,
              value: allSmsProviders.data.ProviderName,
              type: allSmsProviders.data.ProviderName
            },
            { text: "default", value: "default", type: "default" }
          ]
        : allCustomSmsProviders?.data?.CustomGateWayURL != null
          ? [
              {
                text: "Custom Sms Provider",
                value: "Custom Sms Provider",
                type: "Custom Sms Provider"
              },
              { text: "default", value: "default", type: "default" }
            ]
          : [{ text: "default", value: "default", type: "default" }];

    // 3️⃣ Build Email provider options
    const smtpHost = allEmailProviders?.Data?.find((setting: any) => setting.IsDefaultConfig)?.SmtpHost;

    const p = smtpHost ? SMTP_PROVIDERS.find((p) => p.smtpHost === smtpHost) : null;

    const emailProviderOptions = p
      ? [
          { text: p.name, value: p.name, type: p.smtpHost },
          { text: "default", value: "default", type: "default" }
        ]
      : [{ text: "default", value: "default", type: "default" }];

    // 4️⃣ Extract provider properties
    const providerProperties = safeProperties.filter((p) => p.elementType === "emailprovider" || p.elementType === "smsprovider");

    // No provider nodes → nothing to validate
    if (providerProperties.length === 0) {
      return true;
    }

    // 5️⃣ Every provider must match an available option
    return providerProperties.every((property) => {
      if (property.elementType === "emailprovider") {
        return emailProviderOptions.some((opt) => opt.value === property.value);
      }

      if (property.elementType === "smsprovider") {
        return allSmsProvidersOptions.some((opt) => opt.value === property.value);
      }

      return true;
    });
  };

  const validateTemplateSyncNodeProperties = (properties: any[]) => {
    // 1️⃣ If templates are still loading → do NOT fail validation
    if (!smsTemplates.isSuccess || !allEmailTemplates.isSuccess) {
      return true;
    }

    // 2️⃣ Build SMS template options
    const smsTemplatesOptions = [
      { value: "default", type: "verification" },
      { value: "default", type: "welcome" },
      { value: "default", type: "onetimepasscode" },
      { value: "default", type: "forgotpassword" },
      { value: "default", type: "changephoneno" },
      { value: "default", type: "secondfactorauthentication" },
      { value: "default", type: "resetpassword" },
      { value: "default", type: "forgotpin" },
      { value: "suspicious-city-default", type: "suspicious_city_sms_to_user" },
      { value: "suspicious-country-default", type: "suspicious_country_sms_to_user" },
      { value: "suspicious-browser-default", type: "suspicious_browser_sms_to_user" },
      { value: "suspicious-ip-default", type: "suspicious_ip_sms_to_user" },
      { value: "suspicious-device-default", type: "suspicious_device_sms_to_user" },
      ...smsTemplates.data.Data.filter((t: any) => t.Name).map((t: any) => ({
        value: t.Name,
        type: t.SmsTemplateType.toLowerCase()
      }))
    ];

    // 3️⃣ Build Email template options
    const emailTemplatesOptions = [
      { value: "default", type: "registration" },
      { value: "default", type: "welcome" },
      { value: "default", type: "oneclicksignin" },
      { value: "default", type: "forgotpassword" },
      { value: "default", type: "deleteaccount" },
      { value: "default", type: "add_email" },
      { value: "default", type: "secondfactorauthentication" },
      { value: "default", type: "resetpassword" },
      { value: "default", type: "forgotpin" },
      ...allEmailTemplates.Data.filter((t: any) => t.TemplateName).map((t: any) => ({
        value: t.TemplateName,
        type: t.TemplateType.toLowerCase()
      }))
    ];

    // 4️⃣ Validate only if template properties exist
    const templateProperties = properties.filter((p) => p.elementType === "smstemplatename" || p.elementType === "emailtemplatename");

    // No template node → no template validation needed
    if (templateProperties.length === 0) {
      return true;
    }

    // 5️⃣ Every template property must match an option
    return templateProperties.every((property) => {
      if (property.elementType === "smstemplatename") {
        return smsTemplatesOptions.some((opt) => opt.value === property.value);
      }

      if (property.elementType === "emailtemplatename") {
        return emailTemplatesOptions.some((opt) => opt.value === property.value);
      }

      return true;
    });
  };

  const featureValidationCheck = (id: string, mfafeatures: any, appFeatures: any, captchaSetting: any): boolean => {
    const nodeType = getNodeById(id)?.type;
    if (nodeType === "captcha") {
      // For captcha node, check if CAPTCHA is enabled in settings
      return !captchaSetting?.IsEnabled;
    }
    // Find the node config in WorkflowNodeConfig
    let featurePath = "";
    for (const section of WorkflowNodeConfig) {
      for (const configNode of section.nodes) {
        if (configNode.type === nodeType && configNode.feature) {
          featurePath = configNode.feature;
          break;
        }
      }
      if (featurePath) break;
    }
    if (!featurePath) return false;
    // Instead of duplicating logic:
    return !isFeatureEnabled(featurePath, { mfa: mfafeatures, ...appFeatures });
  };
  const nodeValidator = (id: string, properties?: any[]) => {
    const safeProperties = Array.isArray(properties) ? properties : [];

    const hasPropertyError = !!validateNodeProperties(safeProperties);
    const isProviderSynced = validateProviderSyncNodeProperties(safeProperties);
    const isTemplateSynced = validateTemplateSyncNodeProperties(safeProperties);
    const hasFeatureError = featureValidationCheck(id, mfafeatures, allFeatures, captchaSetting);
    const hasMFAFeatureError = validateMFAFeatureProperties(id, safeProperties, mfafeatures);
    const hasProviderType = safeProperties.some((property) => property.elementType === "emailprovider" || property.elementType === "smsprovider");

    setIsPropertyConfigError((prev: any) => {
      const newMap = new Map(prev);

      if (hasProviderType) {
        newMap.set(id, hasPropertyError || !isProviderSynced || !isTemplateSynced || hasFeatureError || hasMFAFeatureError);
      } else {
        newMap.set(id, hasPropertyError || hasFeatureError || hasMFAFeatureError);
      }

      return newMap;
    });
  };

  function validateMFAFeatureProperties(id: string, properties, mfafeatures: any): boolean {
    const mfaNodeTypes = ["mfastate", "configuremfa", "mfaunconfiguredstate"];
    const nodeType = getNodeById(id)?.type;
    if (mfaNodeTypes.includes(nodeType)) {
      // Map UI method names to mfafeatures keys
      const mfaMethodMap = {
        "Push Auth": "IsPushAuthenticatorEnabled",
        "Duo Auth": "IsDuoAuthenticatorEnabled",
        Passkey: "IsPasskeyMFAEnabled",
        "Security Question": "IsSecurityQuestionAsMFAEnabled",
        "Email OTP": "IsEmailOtpAuthenticatorEnabled",
        "SMS OTP": "IsSmsOtpAuthenticatorEnabled",
        Authenticator: "IsAuthenticatorEnabled"
      };
      // Gather all selected values for mfamethods
      const selectedValues = properties.filter((prop: any) => prop.elementType === "mfamethods").flatMap((prop: any) => (Array.isArray(prop.value) ? prop.value : []));
      // Only check for values that are mapped in mfaMethodMap
      const isInvalid = selectedValues.filter((val: string) => mfaMethodMap[val]).some((val: string) => !mfafeatures?.Data?.[mfaMethodMap[val]]);
      return isInvalid;
    }
    return false; // Return 0 for non-MFA node types
  }
  // const validateNodePropertyValues = (properties) =>{
  //   return properties.find((property)=>{
  //    return property.type === "number" && (Number(property.value) > property.max || Number(property.value) < property.min)
  //   })
  // }
  const checkValidationError = (properties) => {
    for (const key in isValidationError) {
      const childProperty = properties.find((property: any) => {
        return property.id === key;
      });
      if (!childProperty) continue;
      const parentProperty = properties.find((property: any) => {
        return property.id === childProperty?.parentPropertyId;
      });
      if (
        parentProperty
          ? Array.isArray(parentProperty.value)
            ? Array.isArray(childProperty.parentPropertyValue)
              ? childProperty.parentPropertyValue.some((value) => parentProperty.value.includes(value)) && isValidationError[key]
              : parentProperty.value.includes(childProperty.parentPropertyValue) && isValidationError[key]
            : Array.isArray(childProperty.parentPropertyValue)
              ? childProperty.parentPropertyValue.includes(parentProperty.value) && isValidationError[key]
              : parentProperty.value === childProperty.parentPropertyValue && isValidationError[key]
          : isValidationError[key]
      ) {
        return true; // If any value is true, return true
      }
    }
    return false; // If no value is true, return false
  };
  const checkPolicyValidationError = (properties: any) => {
    for (const key in isPolicyValidationError) {
      const childProperty = properties.find((property: any) => {
        return property.id === key;
      });
      if (!childProperty) continue;
      const parentProperty = properties.find((property: any) => {
        return property.id === childProperty?.parentPropertyId;
      });
      if (
        parentProperty
          ? Array.isArray(parentProperty.value)
            ? Array.isArray(childProperty.parentPropertyValue)
              ? childProperty.parentPropertyValue.some((value) => parentProperty.value.includes(value)) && isPolicyValidationError[key]
              : parentProperty.value.includes(childProperty.parentPropertyValue) && isPolicyValidationError[key]
            : Array.isArray(childProperty.parentPropertyValue)
              ? childProperty.parentPropertyValue.includes(parentProperty.value) && isPolicyValidationError[key]
              : parentProperty.value === childProperty.parentPropertyValue && isPolicyValidationError[key]
          : isPolicyValidationError[key]
      ) {
        return true; // If any value is true, return true
      }
    }
    return false; // If no value is true, return false
  };

  const updateStateOnNodesDelete = (id: string) => {
    setSelectedNode(null);
    setTree((tr: any) => {
      const copy = { ...tr };
      if (copy.entryNodeId === id) {
        copy.entryNodeId = "";
      }

      nodes.forEach((node: any) => {
        if (node.parentNode && node.parentNode === id) {
          delete copy.nodes[node.id];
        }
      });

      nodes.forEach((node: any) => {
        if (node.id === id && !node.isChildNode) {
          delete copy.nodes[id];
        }
      });

      for (const eachNode in copy.nodes) {
        if (!copy.nodes[eachNode]) continue;
        copy.nodes[eachNode].connections.map((elem: any) => {
          Object.keys(elem).forEach((key: any) => {
            return (elem[key] = elem[key] === id ? "" : elem[key]);
          });
          return elem;
        });
      }
      return copy;
    });

    setNodes((nds: any) => {
      const mappedArray = nds.map((node: any) => {
        node.nodes = node.nodes
          ? node.nodes.filter((nd: any) => {
              return nd.id !== id;
            })
          : null;
        return node;
      });
      const filteredArray = mappedArray.filter((node: any) => {
        return node.id !== id;
      });
      const finalArray = filteredArray.filter((node: any) => {
        if (node.parentNode) {
          if (node.parentNode !== id) {
            return node;
          } else return null;
        }
        return node;
      });
      return finalArray;
    });
    setEdges((eds: any) =>
      eds.filter((edge: any) => {
        return edge.source !== id && edge.target !== id;
      })
    );
    setIsPropertyConfigError((prev) => {
      prev.delete(id);
      return prev;
    });

    nodes.forEach((node: any) => {
      if (node.id === id && node.type === "webpage" && node.nodes) {
        node.nodes.map((node: any) => {
          setIsPropertyConfigError((prev) => {
            prev.delete(node.id);
            return prev;
          });
          return node;
        });
      }

      return node;
    });
  };
  const updateDynamicProperties = (type: any, initialId: string, recursiveId: any, visitedNodesIdArray: any[] = []) => {
    if (
      nodes.find((node: any) => {
        return node.id === recursiveId && node.type === "webpage";
      })
    ) {
      const foundNode = nodes.find((node: any) => {
        return node.id === recursiveId;
      });
      setNodes((nds: any) =>
        nds.map((node: any) => {
          if (node.id === initialId) {
            node.data.properties.find((property: any) => {
              return property.id === "1" && Array.isArray(property.options)
                ? property.options.splice(
                    0,
                    property.options.length,
                    ...(foundNode
                      ? foundNode.nodes
                          .filter((node: any) => {
                            return type === "auth" ? SUPPORTED_PROVIDERS.includes(node.type) : type === "sendotp" ? node.type === "email" || node.type === "phone" : null;
                          })
                          .map((node: any) => {
                            return {
                              text: node.data.label,
                              value: node.type
                            };
                          })
                      : null)
                  )
                : null;
            });
          }

          return node;
        })
      );
    }
    if (!recursiveId) {
      setNodes((nds: any) =>
        nds.map((node: any) => {
          if (node.id === initialId) {
            node.data.properties.find((property: any) => {
              if (property.type === "select" && property.id === "1") {
                property.options.length = 0;
                property.value = "";
              }
            });
          }

          return node;
        })
      );

      return;
    } else if (tree.entryNodeId === recursiveId) {
      visitedNodesIdArray.length = 0;

      return;
    }

    const nextNodeIdArray = Object.keys(tree.nodes).filter((node: any) => {
      if (!tree.nodes[node]) return false;
      return tree.nodes[node].connections.find((conn: any) => {
        return conn.output === recursiveId || conn.true === recursiveId || conn.false === recursiveId;
      });
    });

    visitedNodesIdArray.push(recursiveId);

    const updatedNextNodeArray = nextNodeIdArray.filter((element) => !visitedNodesIdArray.includes(element));
    updatedNextNodeArray.length
      ? updatedNextNodeArray.map((nextNodeId: any) => {
          // console.log(visitedNodesIdArray);
          return updateDynamicProperties(
            type,
            initialId,
            nextNodeId,

            visitedNodesIdArray
          );
        })
      : updateDynamicProperties(
          type,
          initialId,
          null,

          visitedNodesIdArray
        );
    return;
  };

  const disallowedConnections: { [key: string]: string[] } = {
    // disallow connections from key node to value node
    auth: ["mfastate", "configuremfa", "mfaunconfiguredstate"]
  };

  // Utility function to get node label by ID
  function getNodeLabelById(id) {
    const node = nodes.find((n) => n.id === id);
    return node?.data?.label || id;
  }
  return (
    <StateContext.Provider
      value={{
        selectedNode,
        setSelectedNode,
        tree,
        setTree,
        nodes,
        setNodes,
        edges,
        setEdges,
        dragOverParentNode,
        setDragOverParentNode,
        setDraggedNode,
        draggedNode,
        policies,
        setPolicies,
        validateNodeProperties,
        isPropertyConfigError,
        setIsPropertyConfigError,
        updateStateOnNodesDelete,
        updateDynamicProperties,
        checkParentNodeDrop,
        nodeValidator,
        updatedTree,
        setUpdatedTree,
        isValidationError,
        setIsValidationError,
        isPolicyValidationError,
        setIsPolicyValidationError,
        checkValidationError,
        checkPolicyValidationError,
        isOpenWindow,
        setIsOpenwindow,
        CustomNodeToTreeNode,
        duplicateNode,
        getNodeById,
        getNodeLabelById,
        disallowedConnections,
        isWorkflowChanged,
        getJSON,
        highlightedNodeId,
        setHighlightedNodeId,
        secondHighlightedNodeId,
        setSecondHighlightedNodeId,
        isHighlightModeEnabled,
        setIsHighlightModeEnabled
        // validateNodePropertyValues
      }}
    >
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () => useContext(StateContext);
export { StateContext };
